/**
 * Ülke saatleri ve kurlar (açılır düğme).
 *
 * Dropshipping'de üç zaman dilimi aynı anda önemli: tedarikçi ABD'de,
 * müşteri Japonya'da, kullanıcı Türkiye'de. "Amazon'da şu an gece mi?"
 * sorusunu kafadan hesaplamak yerine ekranda görmek işi hızlandırıyor.
 *
 * Kurlar sunucudan geliyor, tarayıcıdan değil: güvenlik politikası
 * `connect-src 'self'` (bkz. `main.ts`) dış adreslere istek atılmasına izin
 * vermiyor. Sunucu 30 dakika önbellekliyor.
 *
 * ⚠️ Buradaki kur **gösterim amaçlı**. Fiyat hesabında kullanılan kur ayrı
 * tutuluyor: o onaydan geçiyor, şok kontrolü var ve geçmişi saklanıyor
 * (§6.5). İkisini karıştırmak, ekrana bakıp verilen bir kararın fiyat
 * motoruyla uyuşmaması demek olurdu.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { pazar } from '@/lib/pazar';
import { paraOnEki } from '@amzjp/shared';

interface PanoKurlari {
  /** USD karşılığı; panelin kendi parası da var (ör. Almanya panelinde EUR, Kanada'da CAD). */
  kurlar: Partial<Record<string, number>>;
  cekildi: string | null;
  hata: string | null;
}

/** Sıra bilinçli: tedarikçi (ABD) → müşteri (JP) → kullanıcı (TR). */
const SAATLER = [
  { kod: 'ABD', bayrak: '🇺🇸', tz: 'America/New_York' },
  { kod: 'JP', bayrak: '🇯🇵', tz: 'Asia/Tokyo' },
  { kod: 'TR', bayrak: '🇹🇷', tz: 'Europe/Istanbul' },
  { kod: 'UK', bayrak: '🇬🇧', tz: 'Europe/London' },
  { kod: 'EU', bayrak: '🇪🇺', tz: 'Europe/Berlin' },
  { kod: 'MX', bayrak: '🇲🇽', tz: 'America/Mexico_City' },
  { kod: 'SA', bayrak: '🇸🇦', tz: 'Asia/Riyadh' },
] as const;

const SAAT_ADI: Record<(typeof SAATLER)[number]['kod'], string> = {
  ABD: 'ABD (New York)',
  JP: 'Japonya',
  TR: 'Türkiye',
  UK: 'İngiltere',
  EU: 'Almanya',
  MX: 'Meksika',
  SA: 'Suudi Arabistan',
};

/** USD karşılığı gösterilecek para birimleri ve biçimleri. */
const KURLAR = [
  { kod: 'JPY', simge: '¥', basamak: 2 },
  { kod: 'TRY', simge: '₺', basamak: 2 },
  { kod: 'EUR', simge: '€', basamak: 3 },
  { kod: 'GBP', simge: '£', basamak: 3 },
  { kod: 'MXN', simge: 'MX$', basamak: 3 },
  { kod: 'SAR', simge: 'SAR ', basamak: 3 },
] as const;

/**
 * Listedeki saatler + panelin kendi pazarı (listede o saat dilimi yoksa). 18 Ekim 2026'da
 * eklenen pazarlarda (ör. Kanada, Singapur) pazarın saati de açılır listede görünsün.
 */
function saatler(): ReadonlyArray<{ kod: string; bayrak: string; tz: string; ad: string }> {
  const liste = SAATLER.map((s) => ({ ...s, ad: SAAT_ADI[s.kod] }));
  const p = pazar();
  return liste.some((s) => s.tz === p.saatDilimi)
    ? liste
    : [...liste, { kod: p.kod, bayrak: p.bayrak, tz: p.saatDilimi, ad: p.ad }];
}

/** Listedeki kurlar + panelin kendi parası (listede yoksa). */
function kurlarListesi(): ReadonlyArray<{ kod: string; simge: string; basamak: number }> {
  const p = pazar();
  return KURLAR.some((k) => k.kod === p.paraBirimi)
    ? KURLAR
    : [...KURLAR, { kod: p.paraBirimi, simge: paraOnEki(), basamak: 3 }];
}

function saatYaz(tz: string, an: Date): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(an);
}

/** Dakikada bir güncellenen an + sunucudan gelen gösterim kurları. */
function useSaatKur() {
  const [an, setAn] = useState(() => new Date());

  useEffect(() => {
    /*
     * Dakikada bir yetiyor — saniye göstermiyoruz. Saniyelik tik, hiçbir
     * bilgi eklemeden her saniye yeniden çizim yaptırırdı.
     */
    const t = setInterval(() => setAn(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const pano = useQuery({
    queryKey: ['fx', 'pano'],
    queryFn: () => api.get<PanoKurlari>('/fx/pano'),
    // Sunucu zaten 30 dk önbellekliyor; istemci de boşuna sormasın
    staleTime: 30 * 60_000,
    refetchInterval: 30 * 60_000,
  });

  return { an, kurlar: pano.data?.kurlar ?? {}, cekildi: pano.data?.cekildi ?? null };
}

function kurYaz(deger: number, basamak: number): string {
  return deger.toLocaleString('tr-TR', {
    minimumFractionDigits: basamak,
    maximumFractionDigits: basamak,
  });
}

/**
 * Saat/kur düğmesi: yalnız panelin pazar saati ve kuru görünüyor (günün işi onlara bağlı);
 * tıklayınca bütün saatler ve kurlar açılıyor. Geniş ekranda üst çubukta, daha dar ekranda
 * ana sayfanın başlığında (23 Eki 2026'ya kadar orada tam şerit vardı; sahip: "küçük ekrana
 * geçince böyle full olmasın, açılır kapanır şekilde olsun").
 */
export function SaatKurDugmesi({ className }: { className?: string }) {
  const { an, kurlar, cekildi } = useSaatKur();
  const [acik, setAcik] = useState(false);
  const kutu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!acik) return;
    const disari = (e: MouseEvent) => {
      if (kutu.current && !kutu.current.contains(e.target as Node)) setAcik(false);
    };
    const tus = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAcik(false);
    };
    document.addEventListener('mousedown', disari);
    document.addEventListener('keydown', tus);
    return () => {
      document.removeEventListener('mousedown', disari);
      document.removeEventListener('keydown', tus);
    };
  }, [acik]);

  // Pazarın saati ve kuru (JP: Tokyo + yen; UK: Londra + sterlin; MX: Mexico City + peso; SA: Riyad + riyal)
  const p = pazar();
  const pazarKuru = kurlar[p.paraBirimi];

  return (
    <div ref={kutu} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setAcik((v) => !v)}
        aria-expanded={acik}
        aria-haspopup="dialog"
        aria-label="Ülke saatleri ve kurlar"
        className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-bg-sunken px-3 text-xs text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg"
      >
        <span aria-hidden="true">{p.bayrak}</span>
        <span className="font-mono tabular-nums text-fg">{saatYaz(p.saatDilimi, an)}</span>
        {pazarKuru !== undefined && (
          <>
            <span className="h-3.5 w-px bg-border" aria-hidden="true" />
            <span className="font-mono tabular-nums text-fg">
              {paraOnEki()}
              {kurYaz(pazarKuru, p.paraBirimi === 'JPY' ? 2 : 3)}
            </span>
          </>
        )}
        <ChevronDown
          className={cn('size-3.5 transition-transform', acik && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {acik && (
        <div
          role="dialog"
          aria-label="Ülke saatleri ve kurlar"
          className="menu-acilis absolute right-0 top-[calc(100%+0.625rem)] z-50 w-64 rounded-[var(--radius-lg)] border border-border bg-bg-elevated p-2 text-[0.8125rem] shadow-[var(--shadow-pop)]"
        >
          <p className="px-3 pb-1.5 pt-1 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
            Saatler
          </p>
          <ul>
            {saatler().map((s) => (
              <li key={s.kod} className="flex items-center justify-between px-3 py-1.5">
                <span className="flex items-center gap-2 text-fg-muted">
                  <span aria-hidden="true">{s.bayrak}</span>
                  {s.ad}
                </span>
                <span className="font-mono tabular-nums text-fg">{saatYaz(s.tz, an)}</span>
              </li>
            ))}
          </ul>
          {Object.keys(kurlar).length > 0 && (
            <>
              <p className="mt-1 border-t border-border px-3 pb-1.5 pt-2.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-fg-subtle">
                1 dolar
              </p>
              <ul>
                {kurlarListesi().map((k) => {
                  const deger = kurlar[k.kod];
                  if (deger === undefined) return null;
                  return (
                    <li key={k.kod} className="flex items-center justify-between px-3 py-1.5">
                      <span className="text-fg-muted">{k.kod}</span>
                      <span className="font-mono tabular-nums text-fg">
                        {k.simge}
                        {kurYaz(deger, k.basamak)}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {cekildi && (
                <p className="px-3 pb-1 pt-1.5 text-[0.6875rem] text-fg-subtle">
                  Güncellendi: {new Date(cekildi).toLocaleString('tr-TR')}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
